import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import AsyncStorage from '@react-native-async-storage/async-storage';
import type { OnboardingData } from '../types';
import { saveOnboardingData } from '../services/onboarding';

interface OnboardingStore {
  currentStep: number;
  data: OnboardingData;
  isSaving: boolean;
  saveError: string | null;
  setStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  updateData: (updates: Partial<OnboardingData>) => void;
  completeOnboarding: () => Promise<{ success: boolean; error?: string }>;
  resetOnboarding: () => void;
}

const initialData: OnboardingData = {
  name: '',
  birthDate: null,
  birthTime: null,
  birthTimeKnown: false,
  birthTimePrecision: 'unknown',
  birthPlace: '',
  birthLat: null,
  birthLng: null,
  timezone: null,
  methodology: ['western'],
  purpose: [],
  focusAreas: [],
};

export const useOnboardingStore = create<OnboardingStore>()(
  persist(
    (set, get) => ({
      currentStep: 0,
      data: initialData,
      isSaving: false,
      saveError: null,
      setStep: (step) => set({ currentStep: step }),
      nextStep: () => set((state) => ({ currentStep: state.currentStep + 1 })),
      prevStep: () =>
        set((state) => ({
          currentStep: Math.max(0, state.currentStep - 1),
        })),
      updateData: (updates) =>
        set((state) => ({
          data: { ...state.data, ...updates },
        })),
      completeOnboarding: async () => {
        set({ isSaving: true, saveError: null });
        const { data } = get();

        // Mock chart data based on the design screens (Scorpio Sun, Pisces Moon, Leo Rising)
        const chartData = {
          sunSign: data.sunSign || 'Scorpio',
          moonSign: 'Pisces',
          risingSign: 'Leo',
        };

        const result = await saveOnboardingData(data, chartData);

        set({
          isSaving: false,
          saveError: result.error || null,
        });

        return result;
      },
      resetOnboarding: () =>
        set({ currentStep: 0, data: initialData, isSaving: false, saveError: null }),
    }),
    {
      name: 'veya-onboarding-storage',
      storage: createJSONStorage(() => AsyncStorage),
    }
  )
);
